import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { reportTemplateEndpoints } from '@/lib/api/endpoints';
import { reportTemplateKeys } from '@/lib/queryKeys';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api/client';
import type { ReportSectionWire } from '@/lib/api/types/reports';

// Doc-editor redo — a template is name + settings (stateFlow / signatureConfig
// / allowEstimateSpawn / intro / outro) + authored sections whose bodies are
// TipTap doc JSON (plan §5).

/** StateFlowConfig — kept as loose JSON in the schema; these keys drive the UI. */
export interface ReportTemplateStateFlow {
  requiresReview?: boolean;
  requiresSignature?: boolean;
  [key: string]: unknown;
}

/** SignatureConfig (spec §8) — null when the template collects no signature. */
export interface ReportTemplateSignatureConfig {
  signerTypes: string[];
  customerSignatureLabel?: string;
  lockOnSignature?: boolean;
  captureMethod?: string;
  [key: string]: unknown;
}

/** Wire shape of a template — list and detail both include ordered sections. */
export interface ReportTemplateSummary {
  id: string;
  name: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  applicableTo: ('JOB' | 'LEAD' | 'CUSTOMER')[];
  serviceTypes: string[];
  allowEstimateSpawn: boolean;
  version: number;
  stateFlow: ReportTemplateStateFlow | null;
  signatureConfig: ReportTemplateSignatureConfig | null;
  customerFacingIntro: string | null;
  customerFacingOutro: string | null;
  sections: ReportSectionWire[];
}

export function useReportTemplates() {
  return useApiQuery<ReportTemplateSummary[]>({
    queryKey: reportTemplateKeys.list(),
    queryFn: async () => apiGet(operationsClient, reportTemplateEndpoints.list()),
    showErrorToast: false,
  });
}

export function useReportTemplate(id: string | undefined) {
  return useApiQuery<ReportTemplateSummary>({
    queryKey: id ? reportTemplateKeys.detail(id) : ['reportTemplates', 'none'],
    queryFn: async () => apiGet(operationsClient, reportTemplateEndpoints.detail(id as string)),
    enabled: Boolean(id),
  });
}

export interface UpdateReportTemplateInput {
  name?: string;
  applicableTo?: ('JOB' | 'LEAD' | 'CUSTOMER')[];
  serviceTypes?: string[];
  stateFlow?: ReportTemplateStateFlow;
  allowEstimateSpawn?: boolean;
  signatureConfig?: ReportTemplateSignatureConfig | null;
  customerFacingIntro?: string;
  customerFacingOutro?: string;
}

export function useUpdateReportTemplate(id: string) {
  const qc = useQueryClient();
  return useApiMutation<ReportTemplateSummary, UpdateReportTemplateInput>({
    mutationFn: async (input) =>
      apiPatch(operationsClient, reportTemplateEndpoints.update(id), input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportTemplateKeys.detail(id) });
      qc.invalidateQueries({ queryKey: reportTemplateKeys.list() });
    },
  });
}

export function useCreateReportTemplate() {
  const qc = useQueryClient();
  return useApiMutation<ReportTemplateSummary, { name: string }>({
    mutationFn: async (input) => apiPost(operationsClient, reportTemplateEndpoints.create(), input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportTemplateKeys.list() });
    },
  });
}

export function usePublishReportTemplate(id: string) {
  const qc = useQueryClient();
  return useApiMutation<ReportTemplateSummary, void>({
    mutationFn: async () => apiPost(operationsClient, reportTemplateEndpoints.publish(id), {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportTemplateKeys.detail(id) });
      qc.invalidateQueries({ queryKey: reportTemplateKeys.list() });
    },
  });
}

export function useArchiveReportTemplate(id: string) {
  const qc = useQueryClient();
  return useApiMutation<ReportTemplateSummary, void>({
    mutationFn: async () => apiPost(operationsClient, reportTemplateEndpoints.archive(id), {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportTemplateKeys.detail(id) });
      qc.invalidateQueries({ queryKey: reportTemplateKeys.list() });
    },
  });
}

export function useCloneReportTemplate(id: string) {
  const qc = useQueryClient();
  return useApiMutation<ReportTemplateSummary, void>({
    mutationFn: async () => apiPost(operationsClient, reportTemplateEndpoints.clone(id), {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportTemplateKeys.list() });
    },
  });
}

// ─── Template sections (same contract shape as report sections, §2.1) ──────

/** Shape of the cached template detail the section mutations touch. */
interface TemplateDetailCacheShape {
  sections?: ReportSectionWire[];
}

export function useCreateTemplateSection(templateId: string) {
  const qc = useQueryClient();
  return useApiMutation<ReportSectionWire, { title: string; order?: number }>({
    mutationFn: async (input) =>
      apiPost(operationsClient, reportTemplateEndpoints.sections(templateId), input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportTemplateKeys.detail(templateId) });
    },
  });
}

export type UpdateTemplateSectionInput = {
  sectionId: string;
  title?: string;
  subtitle?: string;
  content?: unknown;
  customerVisible?: boolean;
  pageBreakAfter?: boolean;
  hiddenWhenEmpty?: boolean;
};

/**
 * Section autosave + metadata/flag edits — `PATCH /report-templates/:id/
 * sections/:sid`. Content saves happen on a debounce while the author is
 * still typing, so this patches the detail cache instead of invalidating: a
 * refetch racing the next keystroke would clobber the live editor state.
 */
export function useUpdateTemplateSection(templateId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async ({ sectionId, ...patch }: UpdateTemplateSectionInput) =>
      apiPatch(operationsClient, reportTemplateEndpoints.section(templateId, sectionId), patch),
    onSuccess: (_data, { sectionId, ...patch }) => {
      qc.setQueryData(reportTemplateKeys.detail(templateId), (prev: unknown) => {
        const detail = prev as TemplateDetailCacheShape | undefined;
        if (!detail?.sections) return prev;
        return {
          ...detail,
          sections: detail.sections.map((s) => (s.id === sectionId ? { ...s, ...patch } : s)),
        };
      });
    },
  });
}

export function useDeleteTemplateSection(templateId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async (sectionId: string) =>
      apiDelete(operationsClient, reportTemplateEndpoints.section(templateId, sectionId)),
    onSuccess: (_data, sectionId) => {
      qc.setQueryData(reportTemplateKeys.detail(templateId), (prev: unknown) => {
        const detail = prev as TemplateDetailCacheShape | undefined;
        if (!detail?.sections) return prev;
        return { ...detail, sections: detail.sections.filter((s) => s.id !== sectionId) };
      });
      qc.invalidateQueries({ queryKey: reportTemplateKeys.detail(templateId) });
    },
  });
}

/**
 * `POST /report-templates/:id/sections/reorder { sectionIds }` — reorders
 * optimistically so the sidebar doesn't flicker, then reconciles.
 */
export function useReorderTemplateSections(templateId: string) {
  const qc = useQueryClient();
  return useApiMutation<unknown, string[], { previous: unknown }>({
    mutationFn: async (sectionIds: string[]) =>
      apiPost(operationsClient, reportTemplateEndpoints.reorderSections(templateId), {
        sectionIds,
      }),
    onMutate: async (sectionIds) => {
      await qc.cancelQueries({ queryKey: reportTemplateKeys.detail(templateId) });
      const previous = qc.getQueryData(reportTemplateKeys.detail(templateId));
      qc.setQueryData(reportTemplateKeys.detail(templateId), (prev: unknown) => {
        const detail = prev as TemplateDetailCacheShape | undefined;
        if (!detail?.sections) return prev;
        const byId = new Map(detail.sections.map((s) => [s.id, s]));
        const reordered = sectionIds
          .map((id, index) => {
            const section = byId.get(id);
            return section ? { ...section, order: index } : null;
          })
          .filter((s): s is ReportSectionWire => s !== null);
        return { ...detail, sections: reordered };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context) qc.setQueryData(reportTemplateKeys.detail(templateId), context.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: reportTemplateKeys.detail(templateId) });
    },
  });
}
